import { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { format, parseISO } from 'date-fns';
import { formatDuration, formatDecimalHours, consolidateProjects, calculateProjectTotals, getEntryProjectMinutes, getEntryWorkMinutes } from '../utils/timeCalculator';
import type { Profile, TimeEntry } from '../types';
import {
  Users, UserCheck, Clock, Lock, Unlock,
  ChevronRight, AlertCircle, FileText, CheckCircle, MapPin, Briefcase,
  Filter, X, Download, ExternalLink, Edit2
} from 'lucide-react';
import { exportToExcel, getMonthlyReportFileName } from '../utils/excelExport';
import { UserAvatar } from './UserAvatar';
import { PositionBreakdown } from './PositionBreakdown';
import {
  getSupervisorSelectedUserId,
  saveSupervisorSelectedUserId,
  getSupervisorSelectedMonth,
  saveSupervisorSelectedMonth
} from '../utils/preferences';

interface SupervisorProps {
  onEdit?: (entryId: string) => void;
}

export function Supervisor({ onEdit }: SupervisorProps = {}) {
  const {
    monthlyReports,
    reviewMonth,
    fetchUserEntries,
    fetchProfiles,
    projects,
    locations,
    monthAttachments
  } = useData();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Data for the selected user
  const [userEntries, setUserEntries] = useState<TimeEntry[]>([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Load all profiles on mount and when reports update
  useEffect(() => {
    let isCancelled = false;
    fetchProfiles()
      .then(data => {
        if (!isCancelled) {
          setProfiles(data);
          const savedUserId = getSupervisorSelectedUserId();
          if (savedUserId) {
            const matching = data.find(p => p.id === savedUserId);
            if (matching) {
              setSelectedUser(matching);
            }
          }
        }
      })
      .catch(err => {
        if (!isCancelled) console.error('Failed to fetch user profiles:', err);
      })
      .finally(() => {
        if (!isCancelled) setLoadingProfiles(false);
      });
    return () => { isCancelled = true; };
  }, [fetchProfiles, monthlyReports]);

  const getAvailableMonths = useCallback((userId: string, entries: TimeEntry[]) => {
    const monthsSet = new Set<string>();

    // Add months from completed or reviewed reports
    monthlyReports
      .filter(r => r.userId === userId)
      .forEach(r => monthsSet.add(r.month));

    // Also include months where user has logged entries
    entries
      .filter(e => !e.userId || e.userId === userId)
      .forEach(e => {
        if (e.date) {
          monthsSet.add(e.date.slice(0, 7));
        }
      });

    // Sort descending
    return Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
  }, [monthlyReports]);

  // Load selected user's entries & targets when selected user changes
  useEffect(() => {
    if (!selectedUser) {
      setUserEntries([]);
      setSelectedMonth('');
      return;
    }

    let isCancelled = false;
    setLoadingUser(true);
    fetchUserEntries(selectedUser.id)
      .then(entries => {
        if (isCancelled) return;
        setUserEntries(entries);
        const months = getAvailableMonths(selectedUser.id, entries);
        if (months.length > 0) {
          const savedMonth = getSupervisorSelectedMonth();
          const targetMonth = savedMonth && months.includes(savedMonth)
            ? savedMonth
            : months[0];
          setSelectedMonth(targetMonth);
          saveSupervisorSelectedMonth(targetMonth);
        } else {
          setSelectedMonth('');
        }
      })
      .catch(err => {
        if (!isCancelled) console.error('Failed to load user time data:', err);
      })
      .finally(() => {
        if (!isCancelled) setLoadingUser(false);
      });

    return () => { isCancelled = true; };
  }, [selectedUser, fetchUserEntries, getAvailableMonths]);

  const handleExportMonth = async () => {
    if (!selectedUser || !selectedMonth) return;
    const empNo = selectedUser.employeeNumber;
    const initials = selectedUser.initials;
    const isApproved = monthlyReports.some(r => r.userId === selectedUser.id && r.month === selectedMonth && r.reviewed);
    const defaultVer = isApproved ? 'v1.0' : 'v0.1';
    const fileName = getMonthlyReportFileName(selectedMonth, empNo, initials, defaultVer);
    await exportToExcel(monthEntries, projects, locations, selectedMonth, fileName, true, {
      uploadCopy: true,
      employeeNumber: empNo,
      initials,
      userProfile: selectedUser,
      isApproval: isApproved
    });
  };

  const handleApprove = async () => {
    if (!selectedUser || !selectedMonth) return;
    try {
      setActionLoading(true);
      await reviewMonth(selectedUser.id, selectedMonth, true);
      if (monthEntries.length > 0) {
        const empNo = selectedUser.employeeNumber;
        const initials = selectedUser.initials;
        const fileName = getMonthlyReportFileName(selectedMonth, empNo, initials, 'v1.0');
        await exportToExcel(
          monthEntries,
          projects,
          locations,
          selectedMonth,
          fileName,
          false,
          {
            uploadCopy: true,
            employeeNumber: empNo,
            initials,
            userProfile: selectedUser,
            isApproval: true
          }
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve monthly report';
      alert(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlock = async () => {
    if (!selectedUser || !selectedMonth) return;
    if (confirm(`Are you sure you want to revoke approval and unlock the report for User: ${selectedUser.email} for ${format(parseISO(selectedMonth + '-01'), 'MMMM yyyy')}?`)) {
      try {
        setActionLoading(true);
        await reviewMonth(selectedUser.id, selectedMonth, false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to unlock monthly report';
        alert(message);
      } finally {
        setActionLoading(false);
      }
    }
  };

  // Helper to count pending reviews per user
  const getPendingCount = (userId: string) => {
    return monthlyReports.filter(
      r => r.userId === userId && r.completed && !r.reviewed
    ).length;
  };

  const getUserReportForMonth = (userId: string, month: string) => {
    return monthlyReports.find(r => r.userId === userId && r.month === month);
  };

  const getProjectName = useCallback((id: string) => projects.find(p => p.id === id)?.name || 'Unknown', [projects]);
  const getLocationName = useCallback((id: string) => locations.find(l => l.id === id)?.name || 'Unknown', [locations]);

  // All entries for selected month
  const monthEntries = userEntries
    .filter(e => e.date.slice(0, 7) === selectedMonth)
    .sort((a, b) => b.date.localeCompare(a.date));

  const totalMinutes = monthEntries.reduce(
    (sum, e) => sum + Math.max(0, getEntryWorkMinutes(e)),
    0
  );

  // Consolidated project totals for the month
  const projectTotals = calculateProjectTotals(monthEntries, getProjectName);

  const selectedProjectInfo = selectedProjectId
    ? projectTotals.find(p => p.projectId === selectedProjectId)
    : null;

  // Entries filtered by selected project if active
  const displayedEntries = monthEntries.filter(entry => {
    if (!selectedProjectId) return true;
    return getEntryProjectMinutes(entry, selectedProjectId) > 0;
  });

  const report = selectedUser ? getUserReportForMonth(selectedUser.id, selectedMonth) : null;
  const availableMonths = selectedUser ? getAvailableMonths(selectedUser.id, userEntries) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 min-h-[calc(100vh-12rem)] animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Sidebar: Users List */}
      <div className="lg:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
        <div className="p-6 border-b border-slate-200 bg-slate-50/50">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            User Directory
          </h2>
          <p className="text-sm text-slate-500 mt-1">Select a user to review monthly reports.</p>
        </div>

        {loadingProfiles ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : profiles.length === 0 ? (
          <div className="flex-1 p-8 text-center text-slate-400">No users found.</div>
        ) : (
          <ul className="divide-y divide-slate-100 overflow-y-auto max-h-[500px] lg:max-h-none flex-1">
            {profiles.map(p => {
              const isSelected = selectedUser?.id === p.id;
              const pendingCount = getPendingCount(p.id);

              return (
                <li key={p.id}>
                  <button
                    onClick={() => {
                      setSelectedUser(p);
                      saveSupervisorSelectedUserId(p.id);
                      setSelectedProjectId(null);
                    }}
                    className={`w-full text-left px-6 py-4 flex items-center justify-between transition-all hover:bg-slate-50/80 ${isSelected ? 'bg-indigo-50/60 border-l-4 border-indigo-600 pl-5' : ''}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar
                        profile={p}
                        size="md"
                        className={isSelected ? 'ring-2 ring-indigo-600 ring-offset-2' : ''}
                      />
                      <div className="min-w-0">
                        <p className={`font-semibold text-slate-800 truncate text-sm ${isSelected ? 'text-indigo-900 font-bold' : ''}`}>
                          {[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5 capitalize">
                          <span className={`w-1.5 h-1.5 rounded-full ${p.role === 'supervisor' ? 'bg-indigo-500' : 'bg-slate-300'}`}></span>
                          {p.role}
                          {[p.firstName, p.lastName].some(Boolean) && (
                            <span className="text-slate-400 truncate max-w-[120px]">({p.email})</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {pendingCount > 0 && (
                        <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-0.5 rounded-full border border-amber-200 animate-pulse">
                          {pendingCount} pending
                        </span>
                      )}
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isSelected ? 'translate-x-1 text-indigo-500' : ''}`} />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Main Panel: User Time Sheets & Reviews */}
      <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8 flex flex-col min-h-[400px]">
        {!selectedUser ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12 my-auto">
            <div className="bg-indigo-50 p-4 rounded-full text-indigo-600 mb-4 animate-bounce">
              <UserCheck className="w-12 h-12" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No User Selected</h3>
            <p className="text-slate-500 max-w-sm mt-2">
              Select a team member from the directory list on the left to inspect, verify, and approve their monthly time tracking submissions.
            </p>
          </div>
        ) : loadingUser ? (
          <div className="flex-1 flex items-center justify-center p-12 my-auto">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
              <p className="text-sm font-medium text-slate-500">Loading user time records...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6 flex-1 flex flex-col">
            {/* Detail Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <UserAvatar profile={selectedUser} size="lg" />
                <div className="space-y-1">
                  <h3 className="text-xl font-extrabold text-slate-900">
                    {[selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') || selectedUser.email}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                    <span>{selectedUser.email}</span>
                    {(selectedUser.employeeNumber || selectedUser.initials) && (
                      <>
                        <span className="text-slate-300">•</span>
                        {selectedUser.employeeNumber && (
                          <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[11px]">
                            Emp: {selectedUser.employeeNumber}
                          </span>
                        )}
                        {selectedUser.initials && (
                          <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[11px]">
                            {selectedUser.initials}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Month Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 font-medium">Month:</span>
                {availableMonths.length === 0 ? (
                  <span className="text-sm text-slate-400 italic bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                    No reviewable months
                  </span>
                ) : (
                  <select
                    value={selectedMonth}
                    onChange={e => {
                      setSelectedMonth(e.target.value);
                      saveSupervisorSelectedMonth(e.target.value);
                      setSelectedProjectId(null);
                    }}
                    className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white font-semibold focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                  >
                    {availableMonths.map(m => {
                      const rep = getUserReportForMonth(selectedUser.id, m);
                      const statusText = rep?.reviewed ? 'Reviewed' : rep?.completed ? 'Awaiting Review' : 'Open';
                      return (
                        <option key={m} value={m}>
                          {format(parseISO(m + '-01'), 'MMMM yyyy')} ({statusText})
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            </div>

            {/* Empty State when no reviewable months exist */}
            {availableMonths.length === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-slate-50/50 rounded-xl border border-slate-200/60 my-auto">
                <Clock className="w-10 h-10 text-slate-400 mb-3" />
                <h4 className="text-base font-bold text-slate-700">No Reviewable Months</h4>
                <p className="text-xs text-slate-400 max-w-sm mt-1">
                  This user does not have any completed or submitted monthly reports awaiting review or already reviewed.
                </p>
              </div>
            )}

            {/* Selected Month Status & Actions Card */}
            {selectedMonth && (
              <div className="bg-slate-50/50 rounded-xl p-5 border border-slate-200/60 grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
                {/* Stats */}
                <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="space-y-1 bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
                    <span className="text-xs font-semibold text-slate-400 block uppercase tracking-wider">Status</span>
                    {report?.reviewed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        <Lock className="w-3 h-3 text-emerald-600" />
                        Reviewed
                      </span>
                    ) : report?.completed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 animate-pulse">
                        <Clock className="w-3 h-3 text-amber-600" />
                        Awaiting Review
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        Open
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
                    <span className="text-xs font-semibold text-slate-400 block uppercase tracking-wider">Hours Tracked</span>
                    <span className="text-sm font-bold text-indigo-700">
                      {formatDuration(totalMinutes)}
                    </span>
                  </div>
                  <div className="space-y-1 bg-white p-3 rounded-lg border border-slate-100 shadow-sm col-span-2 sm:col-span-1">
                    <span className="text-xs font-semibold text-slate-400 block uppercase tracking-wider">Working Days</span>
                    <span className="text-sm font-bold text-slate-700">
                      {(totalMinutes / 60 / 8.4).toFixed(1)} days
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="md:col-span-4 flex flex-wrap items-center md:justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={handleExportMonth}
                    disabled={monthEntries.length === 0}
                    className="flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 px-3.5 py-2.5 rounded-lg transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    title={`Export time report for ${selectedMonth}`}
                  >
                    <Download className="w-4 h-4 text-slate-500 shrink-0" />
                    <span>Export</span>
                  </button>

                  {/* Customer attachment links for selected user and month */}
                  {(monthAttachments || [])
                    .filter(a => a.month === selectedMonth && a.userId === selectedUser.id)
                    .map(att => (
                      <a
                        key={att.id}
                        href={att.onedriveWebUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Customer attachment: ${att.fileName}`}
                        className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2.5 rounded-lg transition-colors shadow-xs"
                        onClick={(e) => {
                          if (!att.onedriveWebUrl) {
                            e.preventDefault();
                            alert(`Attachment: ${att.fileName}`);
                          }
                        }}
                      >
                        <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="max-w-[130px] truncate">{att.fileName}</span>
                        <ExternalLink className="w-3 h-3 text-emerald-500 shrink-0" />
                      </a>
                    ))}

                  {report?.reviewed ? (
                    <button
                      onClick={handleUnlock}
                      disabled={actionLoading}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 border border-rose-200 text-rose-700 hover:bg-rose-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      <Unlock className="w-4 h-4 shrink-0" />
                      Unlock & Reopen
                    </button>
                  ) : report?.completed ? (
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 px-5 py-2.5 rounded-lg text-sm font-bold shadow-sm shadow-emerald-100 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4 shrink-0" />
                      Approve & Lock
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-400 italic bg-slate-100 px-4 py-2.5 rounded-lg border border-slate-200">
                      <AlertCircle className="w-4 h-4 text-slate-400 shrink-0" />
                      User hasn't submitted yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Project Hours Breakdown */}
            {selectedMonth && (
              <PositionBreakdown
                month={selectedMonth}
                items={projectTotals}
                totalMinutes={totalMinutes}
                selectedProjectId={selectedProjectId}
                onSelectProject={setSelectedProjectId}
                titlePrefix="Project"
              />
            )}

            {/* Time Entries Breakdown */}
            {selectedMonth && (
              <div className="flex-1 flex flex-col space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-500" />
                    Time Sheet Details ({format(parseISO(selectedMonth + '-01'), 'MMMM yyyy')})
                  </h4>
                  {selectedProjectInfo && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-100">
                        <Filter className="w-3.5 h-3.5 text-indigo-500" />
                        Showing: <strong className="font-bold">{selectedProjectInfo.name}</strong> ({formatDuration(selectedProjectInfo.minutes)})
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedProjectId(null)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                        title="Clear project filter"
                      >
                        <X className="w-3.5 h-3.5" />
                        Show All
                      </button>
                    </div>
                  )}
                </div>

                {monthEntries.length === 0 ? (
                  <div className="flex-1 bg-slate-50/30 border border-slate-100 rounded-xl flex items-center justify-center p-12 text-slate-400 italic text-sm">
                    No time entries logged for this user in {format(parseISO(selectedMonth + '-01'), 'MMMM yyyy')}.
                  </div>
                ) : displayedEntries.length === 0 ? (
                  <div className="flex-1 bg-slate-50/30 border border-slate-100 rounded-xl flex flex-col items-center justify-center p-12 text-slate-400 italic text-sm gap-2">
                    <p>No entries found for {selectedProjectInfo?.name || 'this project'} in {format(parseISO(selectedMonth + '-01'), 'MMMM yyyy')}.</p>
                    <button
                      type="button"
                      onClick={() => setSelectedProjectId(null)}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline not-italic cursor-pointer"
                    >
                      Show all entries
                    </button>
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm flex-1">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">
                              {selectedProjectId ? 'Hours on Project' : 'Hours'}
                            </th>
                            <th className="px-4 py-3">Project Allocation & Work Description</th>
                            <th className="px-4 py-3">Location</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {displayedEntries.map(entry => {
                            const workMins = getEntryWorkMinutes(entry);
                            const projMins = selectedProjectId ? getEntryProjectMinutes(entry, selectedProjectId) : 0;
                            const consolidated = consolidateProjects(entry.projects);

                            return (
                              <tr key={entry.id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-4 py-4.5 font-semibold text-slate-700 whitespace-nowrap">
                                  {format(parseISO(entry.date), 'EEE, MMM d')}
                                </td>
                                <td className="px-4 py-4.5 whitespace-nowrap">
                                  {selectedProjectId ? (
                                    <>
                                      <div className="font-bold text-indigo-700 flex items-center gap-1.5">
                                        <span className="text-base">{formatDecimalHours(projMins)}</span>
                                        <span className="text-xs font-normal text-indigo-500/80">({formatDuration(projMins)})</span>
                                        <span className="text-[11px] font-semibold text-indigo-500 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                                          {(projMins / 60 / 8.4).toFixed(1)}d
                                        </span>
                                      </div>
                                      <div className="text-xs text-slate-400 mt-0.5">
                                        Shift: {entry.timeArrived} - {entry.timeLeft} · {formatDecimalHours(workMins)} ({formatDuration(workMins)}) total
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                        <span className="text-base">{formatDecimalHours(workMins)}</span>
                                        <span className="text-xs font-normal text-slate-500">({formatDuration(workMins)})</span>
                                      </div>
                                      <div className="text-xs text-slate-400 mt-0.5">
                                        {entry.timeArrived} - {entry.timeLeft} ({entry.breakDurationMinutes}m break)
                                      </div>
                                    </>
                                  )}
                                </td>
                                <td className="px-4 py-4.5">
                                  <div className="space-y-2">
                                    {consolidated.map((p, idx) => {
                                      const isFilteredMatch = selectedProjectId && p.projectId === selectedProjectId;
                                      return (
                                        <div
                                          key={`${p.projectId}-${idx}`}
                                          className={`text-xs ${selectedProjectId
                                            ? isFilteredMatch
                                              ? 'p-2.5 rounded-lg bg-indigo-50/70 border border-indigo-200'
                                              : 'p-1.5 rounded-lg bg-slate-50/40 border border-slate-100 opacity-60'
                                            : ''
                                            }`}
                                        >
                                          <div className="flex items-center justify-between gap-4">
                                            <span className={`font-semibold flex items-center gap-1 min-w-0 truncate ${isFilteredMatch ? 'text-indigo-900 font-bold' : 'text-slate-700'
                                              }`}>
                                              <Briefcase className={`w-3.5 h-3.5 shrink-0 ${isFilteredMatch ? 'text-indigo-600' : 'text-indigo-400'}`} />
                                              <span className="truncate">{getProjectName(p.projectId)}</span>
                                            </span>
                                            <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] shrink-0 inline-flex items-center gap-1 ${isFilteredMatch
                                              ? 'bg-indigo-600 text-white'
                                              : 'bg-indigo-50 text-indigo-700'
                                              }`}>
                                              <span>{formatDecimalHours(p.minutes)}</span>
                                              <span className="font-normal opacity-80">({formatDuration(p.minutes)})</span>
                                            </span>
                                          </div>
                                          {p.description && (
                                            <p className={`text-xs mt-1 pl-4.5 border-l italic ${isFilteredMatch
                                              ? 'text-indigo-950 border-indigo-300 font-medium'
                                              : 'text-slate-500 border-slate-200'
                                              }`}>
                                              {p.description}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                                <td className="px-4 py-4.5 whitespace-nowrap">
                                  <span className="inline-flex items-center gap-1 text-slate-600 bg-slate-100 px-2 py-0.5 rounded text-xs font-semibold">
                                    <MapPin className="w-3 h-3 text-slate-400" />
                                    {getLocationName(entry.locationId)}
                                  </span>
                                </td>
                                <td className="px-4 py-4.5 whitespace-nowrap text-right">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (selectedUser) {
                                        saveSupervisorSelectedUserId(selectedUser.id);
                                      }
                                      if (selectedMonth) {
                                        saveSupervisorSelectedMonth(selectedMonth);
                                      }
                                      onEdit?.(entry.id);
                                    }}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer"
                                    title="Edit time entry"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
